import Link from "next/link";
import { FaUsers } from "react-icons/fa";
import { MdOutlineLocationOn } from "react-icons/md";
import { RiCalendarEventLine } from "react-icons/ri";
import { TbExternalLink } from "react-icons/tb";
import UserImage from "~/components/users/UserImage";
import { GetAllEventsOutput } from "~/types/EventTypesInferred";
import { cn } from "~/utils/fontsConfig";
import { getFormattedTimeStamp } from "~/utils/time";
import { If } from "../../Conditional";
import { PossiblyExternalLink } from "../EventCard";
import { EventImage } from "../EventImage";
import { EventTag } from "../EventTags";
import { HostTooltip } from "../HostedByV3";
import PinnedTag from "../PinnedTag";
import React from "react";

const CondensedEventList = ({
    events,
    permissions,
}: {
    events?: GetAllEventsOutput[];
    permissions?: {
        isAdmin?: boolean;
        isManager?: boolean;
    };
}) => {
    return (
        <div className="flex w-full flex-col gap-2 px-2 md:px-0">
            {events?.map((event) => (
                <EventListItem
                    key={event.id}
                    event={event}
                    permissions={{
                        ...permissions,
                        isHost: event.isHost,
                    }}
                />
            ))}
        </div>
    );
};

const EventListItem = ({
    event,
    permissions,
}: {
    event: GetAllEventsOutput;
    permissions?: { isAdmin?: boolean; isManager?: boolean; isHost?: boolean };
}) => {
    const hosts = event.hosts.map((h) => h.user);
    const eventLink =
        event.isExternalEvent && event.externalEventUrl
            ? event.externalEventUrl
            : `/events/${event.slug}`;

    return (
        <div className="group relative flex w-full gap-3 rounded-lg bg-v3-neutral p-3">
            <div className="flex w-full max-w-[475px] items-start gap-4">
                <PossiblyExternalLink
                    href={eventLink}
                    riverUrl={`/events/${event.slug}`}
                    permissions={permissions}
                    className="h-full"
                >
                    <div className="relative h-[50px] w-[80px] overflow-hidden rounded-md">
                        <EventImage
                            isReadonly={true}
                            event={event}
                            options={{ type: "cover" }}
                            width={80}
                            height={80}
                            allowAnimations={true}
                        />
                    </div>
                </PossiblyExternalLink>
                <div className="flex h-full w-full min-w-[100px] flex-col">
                    <PossiblyExternalLink
                        href={eventLink}
                        riverUrl={`/events/${event.slug}`}
                        permissions={permissions}
                        className="h-full"
                    >
                        <div className="mb-2 line-clamp-2 h-max min-w-0 max-w-[300px] overflow-hidden text-ellipsis break-words leading-none">
                            {event.name}
                        </div>

                        <div className="flex items-center gap-1 text-sm text-v3-N-300">
                            <RiCalendarEventLine className="h-3 w-3" />
                            <span className="text-sm">
                                {getFormattedTimeStamp(event.startTime, {
                                    isTimeSet: true,
                                    short: true,
                                })}
                            </span>
                        </div>
                        <div
                            className={cn(
                                "flex items-center gap-1 text-sm text-v3-N-300",
                                !event.city && "hidden",
                            )}
                        >
                            <MdOutlineLocationOn className="h-3 w-3" />
                            <span className="text-sm">
                                {event.city} • {event.country}
                            </span>
                        </div>
                    </PossiblyExternalLink>
                    <span className="flex gap-2 md:flex-col">
                        <span
                            className={cn(
                                "flex w-full items-center gap-1 md:hidden",
                                event.hosts.length === 0 && "hidden md:hidden",
                            )}
                        >
                            <div className="line-clamp-2 flex w-full flex-wrap break-words text-sm">
                                <div
                                    className={cn(
                                        "mr-2 flex items-center gap-1 text-sm text-v3-N-300",
                                        (!event.attendees.length ||
                                            event.isExternalEvent) &&
                                            "hidden",
                                    )}
                                >
                                    <FaUsers className="h-3 w-3 md:hidden" />
                                    <span className="text-sm">
                                        {event.attendees.length}
                                    </span>
                                    <span className="hidden text-sm md:block">
                                        Attending
                                    </span>
                                    <span className="items-cente ml-1 flex justify-center text-white md:hidden">
                                        •
                                    </span>
                                </div>
                                <span className="mr-2 w-max text-sm text-v3-N-300">
                                    Hosted by:
                                </span>
                                {hosts.map((h, index) => {
                                    const maybeComma =
                                        index < hosts.length - 1 ? (
                                            <span className="mr-1">,</span>
                                        ) : null;
                                    if (h.organizationName)
                                        return (
                                            <React.Fragment key={index + h.id}>
                                                <Link
                                                    className="flex max-w-max items-center gap-2 text-v3-primary"
                                                    href={`?organization=${h.organizationName}`}
                                                    scroll={false}
                                                >
                                                    <span>
                                                        {h.organizationName}
                                                    </span>
                                                </Link>
                                                {maybeComma}
                                            </React.Fragment>
                                        );
                                    return (
                                        <React.Fragment key={index + h.id}>
                                            <HostTooltip user={h}>
                                                <span className="flex w-max items-center gap-2">
                                                    {h.name}
                                                </span>
                                            </HostTooltip>
                                            {maybeComma}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </span>
                    </span>
                </div>
                <span className="absolute right-2 top-2 flex gap-2">
                    <PinnedTag event={event} size="small" />
                    <If
                        condition={
                            !!event.isExternalEvent && !!event.externalEventUrl
                        }
                    >
                        <Link href={event.externalEventUrl!} target="_blank">
                            <span
                                className={cn(
                                    "flex h-6 w-6 items-center justify-center rounded-lg bg-v3-primary p-1 md:h-8 md:w-8",
                                    !event.isExternalEvent && "hidden",
                                )}
                            >
                                <TbExternalLink className="h-4 w-4 text-black md:h-5 md:w-5" />
                            </span>
                        </Link>
                    </If>
                </span>
            </div>
            <div className="hidden h-[70px] w-full max-w-[210px] flex-wrap gap-2 overflow-hidden md:flex">
                {event.tags?.map((tag) => (
                    <EventTag
                        key={tag}
                        tag={tag}
                        isEditing={false}
                        communitySlug={event.community?.slug || ""}
                    />
                ))}
            </div>
            <span
                className={cn(
                    "hidden w-full max-w-[240px] flex-col gap-1 md:flex",
                    event.hosts.length === 0 && "hidden md:hidden",
                )}
            >
                <span className="text-sm text-v3-N-300">Hosted by</span>
                <div className="flex w-max max-w-[100%] flex-col gap-2">
                    {hosts.map((h, i) => {
                        return (
                            <React.Fragment key={i}>
                                {!!h.organizationName &&
                                !!h.useOrganizationIdentity ? (
                                    <Link
                                        className="flex max-w-max items-center gap-2 text-start hover:text-v3-primary"
                                        href={`?organization=${h.organizationName}`}
                                        scroll={false}
                                    >
                                        <UserImage user={h} iconSize={32} />
                                        <span className="flex items-center gap-2 text-wrap break-words text-start">
                                            {h.organizationName}
                                        </span>
                                    </Link>
                                ) : (
                                    <HostTooltip user={h}>
                                        <span className="flex items-center gap-2 text-wrap break-words text-start">
                                            <UserImage user={h} iconSize={32} />
                                            {h.name}
                                        </span>
                                    </HostTooltip>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </span>
        </div>
    );
};

export default CondensedEventList;
